import React, { useEffect, useState } from "react";
import { useDataRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { PageLoading } from "../../components";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { usePrimaryOrganization } from "../../hooks";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { buildStepsFromWorkflowType } from "./HorizontalStepProgressIndicator";
import { EmailTemplateWithCustomTextInputField } from "./DeleteInstructionsPage";
import { StepNumberCircle } from "@truevault/aquilo";
import { RequestHandlingInstructionsTextarea } from "./RequestHandlingInstructionsTextarea";

type CorrectionInstructionsPageProps = {
  embedded?: boolean;
};

export const CorrectionInstructionsPage = ({
  embedded = false,
}: CorrectionInstructionsPageProps = {}) => {
  const [organization] = usePrimaryOrganization();
  const [correctionInstructions, setCorrectionInstructions] = useState("");

  const { result: fetchedCorrectionInstructions } = useDataRequest({
    queryKey: ["requestHandlingInstructions", organization?.id, "correction"],
    api: () => Api.requestHandlingInstructions.getCorrectionInstructions(organization?.id),
  });

  useEffect(() => {
    setCorrectionInstructions(fetchedCorrectionInstructions ?? "");
  }, [fetchedCorrectionInstructions]);

  const { result: consumerRequestClosedCorrectionSampleMessage, refresh: refreshConsumerMessage } =
    useDataRequest({
      queryKey: ["emailTemplate", organization?.id, "closed-correction"],
      api: () =>
        Api.organization.getOrganizationMessageSample(organization?.id, "closed-correction"),
    });

  if (!organization) {
    if (embedded) {
      return <PageLoading />;
    }

    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  const correctionStepProgressSteps = buildStepsFromWorkflowType(WorkflowType.CORRECTION, 1);
  const handleStepProgressClick = (stepId: string) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth" });
  };
  const showCustomTextInputField =
    consumerRequestClosedCorrectionSampleMessage?.templateHTML?.includes("[[[CUSTOM_TEXT]]]");

  const pageContent = (
    <>
      <div className="tw-boundary">
        <div id="correct-data" className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="1" />
          <div className="text-neutral-900 text-[18px] font-normal">
            Correct data in your records according to the individual request
          </div>
        </div>
        <div className="text-neutral-900 text-[14px] font-normal mt-1.5 ml-[40px]">
          (Optional) Add additional instructions
        </div>
        <RequestHandlingInstructionsTextarea
          rows={4}
          cols={62}
          className="ml-[40px] mt-1.5 block rounded-[5px] bg-white px-3 py-1.5 text-base text-neutral-800 border border-solid border-neutral-400 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
          placeholder="E.g. find the consumer first at the settings."
          value={correctionInstructions}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setCorrectionInstructions(e.target.value)
          }
          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
            await Api.requestHandlingInstructions.updateCorrectionInstructions({
              organizationId: organization.id,
              requestHandlingInstructions: e.target.value,
            });
          }}
        />
      </div>

      <div id="notify-consumer" className="flex items-center gap-3 mt-10">
        <StepNumberCircle text="2" />
        <div className="text-neutral-900 text-[18px] font-normal">Notify consumer</div>
      </div>

      {/* Keep email template rendering out of tw-boundary for default email styles */}
      <div style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "700px" }}>
        <div className="tw-boundary">
          <div className="text-neutral-900 text-[14px]">Consumer Email Notification Template</div>
        </div>

        <div
          style={{
            backgroundColor: "#F9F8FB",
            padding: "32px",
            maxWidth: "736px",
            marginTop: "6px",
          }}
        >
          <p>
            <strong>To:</strong> <span>consumer@example.com</span>
          </p>

          <p>
            <strong>Subject: A message about your request [REQUEST ID#]</strong>
          </p>

          {showCustomTextInputField ? (
            <EmailTemplateWithCustomTextInputField
              orgId={organization.id}
              consumerRequestSampleMessage={consumerRequestClosedCorrectionSampleMessage}
              refreshConsumerMessage={refreshConsumerMessage}
              messageSlug="closed-correction"
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: consumerRequestClosedCorrectionSampleMessage?.templateHTML,
              }}
            ></div>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return pageContent;
  }

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="correction-request"
      nextOnClick={async () => {}}
      nextDisabled={false}
      stepProgressSteps={correctionStepProgressSteps}
      onStepProgressClick={handleStepProgressClick}
    >
      {pageContent}
    </WorkflowSetupPageWrapper>
  );
};
